/**
 * MemberDoulaJourneyTab — where she is up to with a doula, for the staff who
 * work with her.
 *
 * Everything built for the Ohai partnership so far had an audience that was not
 * a WeaveIn user: the partner API answers to Ohai's API key, and the doula
 * portal answers to the doula's own login. A case worker opening this profile
 * holds neither, so the work was reachable by two audiences and invisible to
 * the third — the one actually looking at the member profile.
 *
 * Backed by one call to /api/members/{id}/doula-journey. One call rather than
 * six because the tab is read as a sequence: asked, answered, matched, visited.
 * Six requests would let the parts arrive out of order and render a story that
 * never happened.
 *
 * EVERY SENTENCE IS COMPOSED HERE, NOT ON THE SERVER. The endpoint returns a
 * state (`not_asked` / `declined` / `awaiting_match` / `matched`) and an
 * exclusion code, never prose. A sentence composed server-side renders English
 * to a Spanish-speaking case worker and nothing warns — and the mother this tab
 * was built for speaks Spanish, so her case worker plausibly does too.
 *
 * TWO THINGS THE DESIGN IS DELIBERATE ABOUT
 *
 *   - the EMPTY states say which empty it is. "No data" tells a case worker
 *     nothing she can act on; "she has not been asked yet" and "she said no"
 *     lead to different next steps, and only one of them is a task;
 *   - the consent state is shown even when the answer is no. `share_with_doula`
 *     governs what the DOULA sees, not what staff see — so hiding the state
 *     from staff would make the screen lie by omission about why a doula is
 *     seeing nothing.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import {
  AlertCircle,
  BellOff,
  CalendarDays,
  Check,
  Heart,
  Phone,
  ShieldCheck,
  ShieldOff,
  User,
} from 'lucide-react';

interface OptInDecision {
  answer: string;
  answered_at: string | null;
  source: string | null;
  reason: string | null;
  education_version: string | null;
  is_current: boolean;
}

interface AssignedDoula {
  panel_id: number;
  display_name: string;
  pronouns: string | null;
  languages: string[];
  role: string | null;
  since: string | null;
  has_login: boolean;
}

interface Visit {
  date: string;
  kind: string;
  minutes: number | null;
  counts_against_allowance: boolean;
  exclusion_code: string | null;
}

interface CheckIn {
  feeling: string;
  about: string;
  note: string | null;
  recorded_at: string;
}

interface Journey {
  member_id: number;
  programme: string | null;
  opt_in: OptInDecision | null;
  opt_in_history: OptInDecision[];
  match_status: string | null;
  doula: AssignedDoula | null;
  visits: Visit[];
  visits_used: number;
  visits_allowed: number | null;
  visits_remaining: number | null;
  sharing_with_doula: string;
  checkins: CheckIn[];
  state: string;
}

function when(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function MemberDoulaJourneyTab({ memberId }: { memberId: number | string }) {
  const { t } = useTranslation();
  const [data, setData] = useState<Journey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await authFetchJson<Journey>(`/api/members/${memberId}/doula-journey`));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doula.loadFailed', 'We could not load her doula journey.'));
    } finally {
      setLoading(false);
    }
  }, [memberId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** A thumbs-up is a gesture, so it renders as one word and a colour, not a score. */
  const feeling = (key: string): { label: string; className: string } => {
    switch (key) {
      case 'good':
        return { label: t('doula.feelingGood', 'Good'), className: 'bg-green-100 text-green-800' };
      case 'ok':
        return { label: t('doula.feelingOk', 'Okay'), className: 'bg-amber-100 text-amber-800' };
      case 'struggling':
        return { label: t('doula.feelingStruggling', 'Struggling'), className: 'bg-red-100 text-red-800' };
      default:
        return { label: key, className: 'bg-slate-100 text-slate-700' };
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</div>;
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (!data) return null;

  const saidNo = data.state === 'declined';

  // The four states, each a different next step for whoever is reading.
  const summary = (() => {
    switch (data.state) {
      case 'not_asked':
        return t('doula.stateNotAsked', 'She has not been asked about a doula yet.');
      case 'declined':
        return t('doula.stateDeclined', 'She was asked about a doula and said no. She remains in the programme.');
      case 'awaiting_match':
        return t('doula.stateAwaitingMatch', 'She said yes to a doula and is waiting to be matched.');
      default:
        return t('doula.stateMatched', 'She is working with {{name}}.', {
          name: data.doula?.display_name ?? '',
        });
    }
  })();

  const sharing = (() => {
    switch (data.sharing_with_doula) {
      case 'shared':
        return t('doula.sharingShared', 'She shares her check-ins with her doula.');
      case 'withdrawn':
        return t(
          'doula.sharingWithdrawn',
          'She shared this and has since withdrawn it. Her doula no longer sees it.',
        );
      case 'declined':
        return t('doula.sharingDeclined', 'She has chosen not to share her check-ins with her doula.');
      default:
        return t('doula.sharingNotAsked', 'She has not been asked whether to share her check-ins with her doula.');
    }
  })();

  const whyNotBilled = (code: string | null): string =>
    code === 'no_claim_by_design'
      ? t('doula.notBilledByDesign', 'Not billed by design — recorded care that produces no claim.')
      : t('doula.notBilled', 'Not billed: {{reason}}.', { reason: (code ?? '').replace(/_/g, ' ') });

  return (
    <div className="space-y-6 p-1">
      {/* One honest sentence, before any detail. */}
      <div className="rounded-md border bg-muted/40 p-4">
        <p className="text-base font-medium">{summary}</p>
        {data.programme && <p className="mt-1 text-sm text-muted-foreground">{data.programme}</p>}
      </div>

      {/* ---- The decision ------------------------------------------------ */}
      <section>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <Check className="h-4 w-4" />
          {t('doula.choosingADoula', 'Choosing a doula')}
        </h3>

        {!data.opt_in ? (
          <p className="text-sm text-muted-foreground">
            {t('doula.notAskedYet', 'She has not been asked whether she would like a doula.')}
          </p>
        ) : (
          <div className="rounded-md border p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded px-2 py-0.5 text-xs font-semibold uppercase ${
                  data.opt_in.answer === 'yes'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-slate-200 text-slate-700'
                }`}
              >
                {data.opt_in.answer === 'yes' ? t('common.yes', 'Yes') : t('common.no', 'No')}
              </span>
              <span className="text-sm text-muted-foreground">
                {t('doula.answeredOn', 'answered {{date}}', { date: when(data.opt_in.answered_at) })}
                {data.opt_in.source ? ` · ${data.opt_in.source}` : ''}
              </span>
            </div>
            {data.opt_in.reason && <p className="mt-2 text-sm">{data.opt_in.reason}</p>}
            {/* Saying this on the screen matters: "no" is a recorded answer that
                keeps her in the programme, and a case worker who reads it as a
                withdrawal would act on it wrongly. */}
            {saidNo && (
              <p className="mt-2 text-sm text-muted-foreground">
                {t('doula.stillEnrolled', 'She remains in the programme and can change her mind at any time.')}
              </p>
            )}
            {data.opt_in_history.length > 1 && (
              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  {t('doula.earlierAnswers', '{{count}} earlier answers', {
                    count: data.opt_in_history.length - 1,
                  })}
                </summary>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {data.opt_in_history
                    .filter((h) => !h.is_current)
                    .map((h, i) => (
                      <li key={i}>
                        {h.answer === 'yes' ? t('common.yes', 'Yes') : t('common.no', 'No')} —{' '}
                        {when(h.answered_at)}
                      </li>
                    ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </section>

      {/* ---- Her doula ---------------------------------------------------- */}
      {data.doula && (
        <section>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            <User className="h-4 w-4" />
            {t('doula.herDoula', 'Her doula')}
          </h3>
          <div className="rounded-md border p-4">
            <p className="font-medium">
              {data.doula.display_name}
              {data.doula.pronouns ? (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  {data.doula.pronouns}
                </span>
              ) : null}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {data.doula.role ? `${data.doula.role} · ` : ''}
              {t('doula.since', 'since {{date}}', { date: when(data.doula.since) })}
              {data.doula.languages.length ? ` · ${data.doula.languages.join(', ')}` : ''}
            </p>
            {/* Operationally useful rather than decorative: a doula with no
                login cannot be reached through the portal, and someone has to
                know that before telling a mother her doula will see a message. */}
            {!data.doula.has_login && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-amber-700">
                <BellOff className="h-4 w-4" />
                {t('doula.noPortalLogin', 'She has no portal login yet, so she cannot be messaged in-app.')}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ---- Visits ------------------------------------------------------- */}
      <section>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <CalendarDays className="h-4 w-4" />
          {t('doula.visits', 'Visits')}
        </h3>

        {data.visits_allowed !== null && (
          <p className="mb-2 text-sm">
            <span className="font-medium">
              {t('doula.visitsUsed', '{{used}} of {{allowed}}', {
                used: data.visits_used,
                allowed: data.visits_allowed,
              })}
            </span>{' '}
            {t('doula.visitsRemaining', 'used — {{remaining}} remaining.', {
              remaining: data.visits_remaining ?? 0,
            })}{' '}
            <span className="text-muted-foreground">
              {t(
                'doula.sharedAllowanceNote',
                'This is the shared prenatal and postpartum allowance, not a separate count for each.',
              )}
            </span>
          </p>
        )}

        {data.visits.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('doula.noVisits', 'No visits recorded yet.')}</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {data.visits.map((v, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 p-3 text-sm">
                <span className="font-medium">{when(v.date)}</span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  {v.kind === 'phone' ? <Phone className="h-3.5 w-3.5" /> : null}
                  {t(`doula.visitKind.${v.kind}`, v.kind.replace(/_/g, ' '))}
                </span>
                {v.minutes ? (
                  <span className="text-muted-foreground">
                    {t('doula.minutes', '{{count}} min', { count: v.minutes })}
                  </span>
                ) : null}
                {!v.counts_against_allowance && (
                  <span className="text-muted-foreground">· {whyNotBilled(v.exclusion_code)}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- How she says she is doing ------------------------------------ */}
      <section>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <Heart className="h-4 w-4" />
          {t('doula.howSheIsDoing', 'How she says she is doing')}
        </h3>

        <p className="mb-2 flex items-center gap-1.5 text-sm">
          {data.sharing_with_doula === 'shared' ? (
            <ShieldCheck className="h-4 w-4 text-green-700" />
          ) : (
            <ShieldOff className="h-4 w-4 text-muted-foreground" />
          )}
          <span className={data.sharing_with_doula === 'shared' ? '' : 'text-muted-foreground'}>
            {sharing}
          </span>
        </p>

        {data.checkins.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('doula.noCheckins', 'She has not recorded any check-ins.')}
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {data.checkins.map((c, i) => {
              const f = feeling(c.feeling);
              return (
                <li key={i} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 p-3 text-sm">
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold ${f.className}`}>
                    {f.label}
                  </span>
                  <span className="text-muted-foreground">
                    {c.about === 'baby'
                      ? t('doula.aboutBaby', 'about the baby')
                      : t('doula.aboutMother', 'about herself')}
                  </span>
                  <span className="text-muted-foreground">{when(c.recorded_at)}</span>
                  {c.note && <span className="w-full text-sm">{c.note}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
